import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { from, Subscription } from 'rxjs';
import { ModalService, AuthMode } from '../../../shared/modal.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router'; // <-- ADD THIS
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
})
export class AuthModalComponent implements OnInit, OnDestroy {
  isVisible = false;
  activeMode: AuthMode = 'login';
  loginForm: FormGroup;
  signupForm: FormGroup;
  loading = false;
  showLoginPassword = false;
  showSignupPassword = false;

  private visibilitySubscription: Subscription | undefined;
  private modeSubscription: Subscription | undefined;

  constructor(
    private modalService: ModalService,
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });

    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [''],
      password: ['', [Validators.required, Validators.minLength(6)]],
      termsAccepted: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    this.visibilitySubscription = this.modalService.modalVisibility$.subscribe(
      (visible) => {
        this.isVisible = visible;
        if (!visible) {
          this.loginForm.reset();
          this.signupForm.reset();
          this.loading = false;
        }
      }
    );

    this.modeSubscription = this.modalService.initialAuthMode$.subscribe(
      (mode) => {
        this.activeMode = mode;
        this.loginForm.reset();
        this.signupForm.reset();
      }
    );
  }

  ngOnDestroy(): void {
    this.visibilitySubscription?.unsubscribe();
    this.modeSubscription?.unsubscribe();
  }

  closeModal(): void {
    this.modalService.closeModal();
  }

  switchTo(mode: AuthMode): void {
    this.activeMode = mode;
    this.loginForm.reset();
    this.signupForm.reset();
  }

  togglePasswordVisibility(type: 'login' | 'signup'): void {
    if (type === 'login') {
      this.showLoginPassword = !this.showLoginPassword;
    } else {
      this.showSignupPassword = !this.showSignupPassword;
    }
  }

  onLoginSubmit(): void {
    if (this.loginForm.valid) {
      this.loading = true;
      console.log('Login Submitted:', this.loginForm.value);
      setTimeout(() => {
        this.loading = false;
        this.closeModal();
        this.router.navigate(['/dashboard']);
      }, 1500);
    } else {
      console.error('Login form is invalid');
      this.loginForm.markAllAsTouched();
    }
  }

  onSignupSubmit(): void {
    if (this.signupForm.valid) {
      this.loading = true;
      const signupFormData = this.signupForm.value;
      const signupData = {
        emailId: signupFormData.email,
        phoneNumber: signupFormData.phoneNumber,
        password: signupFormData.password,
      };

      const userData = {
        email: signupFormData.email,
        phoneNumber: signupFormData.phoneNumber,
        password: signupFormData.password,
        isSignup: true,
      };
      this.authService.setUserData(userData);
      this.authService.signup(signupData).subscribe({
        next: (signupResponse) => {
          const otpPayload = {
            otpSentVia: signupData.emailId ? 'EMAIL' : 'SMS',
            email: signupData.emailId,
            phoneNumber: signupData.phoneNumber,
          };

          this.authService.generateOtp(otpPayload).subscribe({
            next: () => {
              this.loading = false;
              localStorage.clear();
              localStorage.setItem('from-signup', 'true');
              this.closeModal();
              this.router.navigate(['/verify-otp']);
            },
            error: (err) => {
              this.loading = false;
              console.error('OTP generation failed', err);
            },
          });
        },
        error: (err) => {
          this.loading = false;
          console.error('Signup failed', err);
        },
      });
    } else {
      console.error('Signup form is invalid');
      this.signupForm.markAllAsTouched();
    }
  }

  get loginEmail() {
    return this.loginForm.get('email');
  }
  get loginPassword() {
    return this.loginForm.get('password');
  }

  get signupEmail() {
    return this.signupForm.get('email');
  }
  get signupPassword() {
    return this.signupForm.get('password');
  }
  get signupPhoneNumber() {
    return this.signupForm.get('phoneNumber');
  }
  get signupTermsAccepted() {
    return this.signupForm.get('termsAccepted');
  }
}
